import { 
  User, 
  Bell, 
  Shield, 
  Smartphone, 
  Globe, 
  Eye, 
  Database,
  Lock,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';

export const Settings = () => {
  const { logout } = useAuth();

  const sections = [
    { id: 'profile', icon: User, label: 'Profile Settings', description: 'Manage your public profile and identity' },
    { id: 'notifications', icon: Bell, label: 'Notifications', description: 'Choose what you want to be notified about' },
    { id: 'security', icon: Shield, label: 'Security', description: 'Biometrics, passwords and active sessions' },
    { id: 'privacy', icon: Eye, label: 'Privacy', description: 'Control who can see your activity and data' },
    { id: 'ecosystem', icon: Globe, label: 'Ecosystem', description: 'Manage connected apps and permissions' },
    { id: 'data', icon: Database, label: 'Data & Storage', description: 'Export or delete your ecosystem data' },
  ];

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-3xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500">Manage your account preferences and ecosystem settings.</p>
      </header>

      <div className="grid gap-8 lg:grid-cols-4">
        <nav className="space-y-1">
          {sections.map((section) => (
            <button
              key={section.id}
              className={`flex w-full items-center space-x-3 rounded-xl px-4 py-3 text-left transition-colors ${
                section.id === 'profile' 
                  ? 'bg-indigo-50 text-indigo-600' 
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              <section.icon size={20} />
              <div>
                <p className="text-sm font-bold">{section.label}</p>
                <p className="text-xs opacity-70 hidden md:block">{section.description}</p>
              </div>
            </button>
          ))}
        </nav>

        <div className="lg:col-span-3 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
              <CardDescription>Update your basic profile information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center space-x-6">
                <div className="relative">
                  <div className="h-24 w-24 rounded-full bg-slate-200" />
                  <button className="absolute bottom-0 right-0 rounded-full bg-indigo-600 p-2 text-white shadow-lg">
                    <User size={16} />
                  </button>
                </div>
                <div className="space-y-1">
                  <h4 className="font-bold text-slate-900">Profile Picture</h4>
                  <p className="text-sm text-slate-500">PNG, JPG up to 10MB</p>
                  <div className="flex space-x-2 pt-2">
                    <Button variant="outline" size="sm">Change</Button>
                    <Button variant="ghost" size="sm" className="text-red-500">Remove</Button>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <Input label="Username" defaultValue="keskedo_pioneer" />
                <Input label="Email" defaultValue="pioneer@keskedo.com" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">Bio</label>
                <textarea 
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  rows={4}
                  defaultValue="Passionate about decentralized governance and building sustainable digital ecosystems."
                />
              </div>
              <div className="flex justify-end">
                <Button>Save Changes</Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Biometric Security</CardTitle>
              <CardDescription>Enhance your account security with WebAuthn</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 p-4">
                <div className="flex items-center space-x-4">
                  <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600">
                    <Smartphone size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">FaceID / Fingerprint</p>
                    <p className="text-xs text-slate-500">Use your device's biometrics to sign in</p>
                  </div>
                </div>
                <Button variant="outline" size="sm">Enable</Button>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 p-4">
                <div className="flex items-center space-x-4">
                  <div className="h-10 w-10 rounded-lg bg-slate-200 flex items-center justify-center text-slate-600">
                    <Lock size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">Security Key</p>
                    <p className="text-xs text-slate-500">Use a physical YubiKey or similar device</p>
                  </div>
                </div>
                <Button variant="outline" size="sm">Add Key</Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-100">
            <CardHeader>
              <CardTitle className="text-red-600">Danger Zone</CardTitle>
              <CardDescription>Irreversible actions for your account</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-900">Sign out from all devices</p>
                  <p className="text-xs text-slate-500">You will be logged out from all active sessions</p>
                </div>
                <Button variant="outline" size="sm" onClick={logout}>Sign Out</Button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-900">Delete Account</p>
                  <p className="text-xs text-slate-500">Permanently remove your data from the ecosystem</p>
                </div>
                <Button variant="danger" size="sm">Delete</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
